import { IconButton, Input, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import "@fontsource/rubik";

import AuthLayout from "../layouts/AuthLayout";
import CustomCard from "../../components/CustomCard";
import CustomUserCard from "../events/components/CustomUserCard";
import MainLogoCard from "../dashboard/components/MainLogoCard";
import AllTeamsCard from "../dashboard/components/AllTeamsCard";
import EventCard from "../dashboard/components/EventCard";
import EventStageFilter from "../dashboard/components/EventStageFilter";
import EventTypeFilter from "../dashboard/components/EventTypeFilter";
import CustomSortOptions from "../events/components/CustomSortOptions";
import filmingTutorialImage from "../../assets/filmingTutorialCard.png";
import butterfly from './../../assets/butterfly_navigation.png';

import useProjectStageFilter from "../dashboard/hooks/useProjectStageFilter";
import useProjectTypeFilter from "../dashboard/hooks/useProjectTypeFilter";
import useEventSort from "../dashboard/hooks/useEventSort";
import useEventTypeList from "../eventcreation/hooks/useEventTypeList";
import useArchiveProject from "../dashboard/hooks/useArchiveProject";
import useArchivedEventList from "./hooks/useArchivedEventList";
import usePermissions from "../../hooks/usePermissions";
import useVideoLengthList from "../eventcreation/hooks/useVideoLengthList";
import useDeleteProject from "../dashboard/hooks/useDeleteProject";

// Every action the cards and the left column can dispatch needs its dialog
// rendered here, or the click sets a redux flag and nothing opens.
import EventCreationDrawer from "../eventcreation";
import PopupVideoPlayer from "../../components/PopupVideoPlayer";
import DeleteConfirmationPopup from "../dashboard/components/DeleteConfirmationPopup";
import DeleteDeclinePopup from "../dashboard/components/DeleteDeclinePopup";
import DeclineEditProjectPopup from "../dashboard/components/DeclineEditProjectPopup";
import TeamCreationDrawer from "../teams";
import DeleteTeamPopup from "../teams/components/DeleteTeamPopup";
import RenameTeamPopup from "../teams/components/RenameTeamPopup";
import InviteOrganisationMembersPopup from "../dashboard/components/InviteOrganisationMembersPopup";
import RemoveOrganisationMemberConfirmationPopup from "../dashboard/components/RemoveOrganisationMemberConfirmationPopup";
import AssignAdminRolePopup from "../dashboard/components/AssignAdminRolePopup";
import ViewOrganisationMembersPopup from "../dashboard/components/ViewOrganisationMembersPopup";

import { eventSortOptions, eventStageOptions } from "../../constants/staticData";
import { updateState } from "../../redux/commonSlice";
import { setSnackbar } from "../../redux/messageSlice";
import { goToCopilot, timeStampToString } from "../../utils/commonServices";
import { viewProjectMembers } from "../events/actions";
import { PERMISSION } from "../../constants/constants";
import { getRoleNamesFromIds, getRoles, getUserRoles } from "../../commonactions/actions";
import { getAllTeams, viewOrganisationMember } from "../dashboard/actions";

const ArchivedProjects = (props) => {
    const commonReducer = useSelector((state) => state.root.commonReducer);
    const teamsReducer = useSelector((state) => state.root.teamsReducer);
    const dropdownReducer = useSelector((state) => state.root.dropdownReducer);
    const createEventReducer = useSelector((state) => state.createEventReducer);
    const permissionsReducer = useSelector((state) => state.permissionsReducer);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [hasPermission] = usePermissions();

    const [archivedEvents, isLoading, refetchArchivedEvents] = useArchivedEventList();
    // Called for their side effects: they populate the project-type and
    // video-length dropdowns that the Duplicate and Edit payloads read.
    useEventTypeList();
    useVideoLengthList();

    const [members, setMembers] = useState(teamsReducer.organization_members);

    const [searchString, setSearchString] = useState("");
    const [searchedArray, setSearchedArray] = useState([]);
    const [selectedProjectStage, setSelectedProjectStage] = useState(['View All']);
    const [selectedProjectTypeValue, setSelectedProjectTypeValue] = useState(['View All']);
    const [selectedSortbyValue, setSelectedSortbyValue] = useState('view_all');

    // Same chain the dashboard uses: search -> stage -> type -> sort
    const [projectStageEvents] = useProjectStageFilter(searchedArray, selectedProjectStage);
    const [projectTypeEvents] = useProjectTypeFilter(projectStageEvents, selectedProjectTypeValue);
    const [sortedEvents] = useEventSort(projectTypeEvents, selectedSortbyValue);

    const [, unarchiveProject] = useArchiveProject(refetchArchivedEvents);
    // Deleting from here has to refresh THIS list, not just the dashboard's.
    const [deleteProject, deleteEventStatus] = useDeleteProject(commonReducer.production_event_id, null, refetchArchivedEvents);

    // permissionsReducer is not persisted - it is a sibling of the persisted
    // `root` slice, so every permission is false after a full page load. Without
    // this, opening /archived-projects directly (bookmark, refresh, pasted URL)
    // left the card menu with Delete, Duplicate and Edit permanently hidden,
    // while reaching the same page from the dashboard showed them.
    useEffect(() => {
        getUserRoles(dispatch, navigate, permissionsReducer)
        getAllTeams(dispatch, navigate)
        if (dropdownReducer.organisation_roles?.length === 0 || dropdownReducer.team_roles?.length === 0 || dropdownReducer.project_roles?.length === 0) {
            getRoles(dispatch, navigate)
        }
        // A dialog flag left set by an earlier page must not open a dialog here
        // on arrival; these live in persisted slices and would otherwise survive.
        dispatch(updateState({ event_delete_popup: false, delete_decline_popup: false, archive_project_popup: false, play_tutorial: false }))
    }, [])

    useEffect(() => {
        hasPermission(PERMISSION.ORGANISATION.MEMBER.VIEW) && viewOrganisationMember(dispatch, navigate, commonReducer)
    }, [permissionsReducer.permissions_master[PERMISSION.ORGANISATION.MEMBER.VIEW]])

    useEffect(() => {
        setMembers(teamsReducer?.organization_members)
    }, [teamsReducer?.organization_members])

    // Mirrors the dashboard: a project with files still uploading cannot be deleted.
    const disableDelete = (e) => {
        const fileList = props.uploadProps?.selectedFiles
        return !!fileList?.find(item => item.extraPayload.event_id == e.id)
    }

    // Match the dashboard's search fields so a term that finds a project there finds it here.
    const matchesSearch = (searchedEvent, term) => {
        if (!term) return true;
        const value = term.toLocaleLowerCase();
        return !!(searchedEvent?.name?.toLowerCase()?.includes(value)
            || searchedEvent?.event_type?.name?.toLowerCase()?.includes(value)
            || timeStampToString(searchedEvent?.start_date)?.toLowerCase()?.includes(value)
            || timeStampToString(searchedEvent?.end_date)?.toLowerCase()?.includes(value));
    };

    //***********Reapplies the active search as soon as the archived list is refetched************ */
    useEffect(() => {
        setSearchedArray(archivedEvents?.filter((searchedEvent) => matchesSearch(searchedEvent, searchString)));
    }, [archivedEvents])

    const onSearch = (event) => {
        setSearchString(event.target.value);
        setSearchedArray(archivedEvents?.filter((searchedEvent) => matchesSearch(searchedEvent, event.target.value)));
    }

    const onEventClick = (event) => {
        dispatch(setSnackbar({ flag: false, type: 'error', message: "ACCESS DENIED!" }))
        dispatch(updateState({ production_event_id: event.id }));
        hasPermission(PERMISSION.PROJECT.MEMBER.VIEW) && viewProjectMembers({ event_id: event?.id }, dispatch, navigate, { commonReducer })
        if (event?.status_type_order <= 3) {
            navigate(`/productionevent/${event.id}`);
        } else {
            navigate(`/completedevent/${event.id}`)
        }
    };

    // Unarchive is the undo - no confirmation dialog.
    const onToggleArchive = (event) => {
        unarchiveProject(event?.id);
    };

    return <AuthLayout uploadProps={props.uploadProps} sx={{ padding: '2% 3.2% 2.1% 2.2%' }}>
        <Box gap={2} className='flex row a-center j-end' sx={{ pr: { xs: 1, sm: 3 } }} >
            {commonReducer?.copilot_access && <img onClick={(event) => goToCopilot(event, dispatch)} src={butterfly} style={{ borderRadius: '5px', width: '230px', cursor: 'pointer' }} />}
            <CustomUserCard />
        </Box>

        <Box className='flex row justify-center align-center' >

            <Box id="left" className='flex col' sx={{ maxWidth: { xs: '160px', sm: '28%', md: '23%', lg: '21%', xl: '22%' } }} >
                <MainLogoCard />
                <CustomCard>
                    <AllTeamsCard />
                </CustomCard>
                <CustomCard
                    margin='20px 0px 0px 0px'
                    sx={{ flex: 2, cursor: 'pointer' }}
                    onClick={() => dispatch(updateState({ play_tutorial: true }))}
                >
                    <img src={filmingTutorialImage} height='100%' style={{ objectFit: 'cover', borderRadius: '5px' }} width='100%' />
                </CustomCard>
            </Box>

            <Box id="right" width='100%' minWidth='300px' ml={4.5}>
                <Box sx={{ width: '100%' }}>
                    <Box gap={1} sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', color: 'black' }}>
                        <IconButton onClick={() => { navigate('/dashboard') }}>
                            <KeyboardBackspaceOutlinedIcon sx={{ color: 'black' }} />
                        </IconButton>
                        <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '10px', sm: '11px', md: '12px', lg: '14px', xl: '20px' } }}>Back to Dashboard</Typography>
                    </Box>
                </Box>

                <Box width='100%' className='flex row' sx={{ marginTop: { xs: '20px', md: '20px', lg: '26px', xl: '34px' }, marginBottom: { xs: '12px', md: '15px', lg: '20px', xl: '30px' }, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Box flex={1}>
                        <Typography sx={{ fontSize: { xs: '12px', sm: '12px', md: '14px', lg: '16.5px', xl: '24px' }, fontWeight: 600 }}>Archived Projects</Typography>
                    </Box>

                    <Box id="search"
                        sx={{ marginRight: { xs: '12px', md: '15px', lg: '18px', xl: '30px' }, maxWidth: { xs: '100px', md: '120px', lg: '150px', xl: '200px' } }}
                    >
                        <Input
                            value={searchString}
                            onChange={onSearch}
                            sx={{
                                borderBottom: 2,
                                borderColor: "#333333",
                                input: {
                                    fontSize: { xs: '12px', md: '13px', lg: '14px', xl: '20px' },
                                    fontWeight: 400,
                                    color: "#000000",
                                },
                            }}
                            placeholder="Search"
                            startAdornment={
                                <IconButton sx={{ paddingBottom: 1, fontSize: { xs: '12px', md: '14px', lg: '17px', xl: '24px' }, '& .MuiSvgIcon-root': { fontSize: { xs: '12px', md: '14px', lg: '17px', xl: '24px' } }, paddingRight: 2 }} >
                                    <SearchIcon style={{ color: "#000000" }} />
                                </IconButton>
                            }
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', cursor: 'pointer' }} gap={{ xs: 0.5, sm: 0.7, md: 0.9, lg: 1.2, xl: 1.4 }} pr={1}>
                        <EventStageFilter
                            sortByOptions={eventStageOptions}
                            selectedProjectStage={selectedProjectStage}
                            setSelectedProjectStage={setSelectedProjectStage}
                            menuTitle="Status"
                        />
                        <EventTypeFilter
                            sortByOptions={eventSortOptions}
                            selectedProjectTypeValue={selectedProjectTypeValue}
                            setSelectedProjectTypeValue={setSelectedProjectTypeValue}
                        />
                        <CustomSortOptions
                            sortByOptions={eventSortOptions}
                            selectedSortbyValue={selectedSortbyValue}
                            setSelectedSortbyValue={setSelectedSortbyValue}
                            menuTitle="Sort By"
                        />
                    </Box>
                </Box>

                <Box id="events" className='flex row f-wrap f-start a-start' sx={{ height: { xs: '71vh', sm: '67vh', md: '64vh', lg: '61vh', xl: '66vh' }, overflowY: "scroll", whiteSpace: 'nowrap', alignContent: 'flex-start', backgroundColor: 'transparent', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { display: 'none' } }} >
                    {
                        isLoading ? <Typography variant="h3" sx={{ color: '#878887', textAlign: 'center', width: '100%', marginTop: '30%' }}>Loading...</Typography>
                            : (sortedEvents?.length > 0 ? sortedEvents?.map((e) => {
                                return <EventCard
                                    key={e.id}
                                    onClick={() => onEventClick(e)}
                                    event={e}
                                    disableDelete={disableDelete(e)}
                                    isArchived
                                    onToggleArchive={onToggleArchive}
                                    sx={{ mt: 2, mr: 2, cursor: 'pointer' }}
                                />
                            }) :
                                <Box className='flex col j-center a-center' sx={{ overflowY: "initial", whiteSpace: 'normal' }} height='100%' width='100%'>
                                    <Typography sx={{ fontSize: 30, color: '#878887', fontWeight: 300, textAlign: 'center', mt: 3 }} >
                                        {archivedEvents?.length > 0 ? 'No archived projects match these filters.' : 'You have not archived any projects.'}
                                    </Typography>
                                </Box>)
                    }
                </Box>
            </Box>
        </Box>

        {createEventReducer.open_form == true && <EventCreationDrawer />}

        {commonReducer.play_tutorial == true && <PopupVideoPlayer tutorial />}

        {commonReducer.event_delete_popup == true && <DeleteConfirmationPopup deleteProject={deleteProject} status={deleteEventStatus} />}

        {commonReducer.delete_decline_popup == true && <DeleteDeclinePopup />}

        {createEventReducer.edit_decline_popup == true && <DeclineEditProjectPopup />}

        {teamsReducer.open_create_team_form == true && <TeamCreationDrawer />}

        {teamsReducer.delete_team_popup == true && <DeleteTeamPopup />}

        {teamsReducer.rename_team_popup == true && <RenameTeamPopup />}

        {teamsReducer.invite_team_members_popup == true && <InviteOrganisationMembersPopup members={members} options={dropdownReducer?.organisation_roles} roleNameFromId={getRoleNamesFromIds} />}

        {teamsReducer.remove_member_confirmation_popup == true && <RemoveOrganisationMemberConfirmationPopup />}

        {teamsReducer.assign_role_popup == true && <AssignAdminRolePopup />}

        {teamsReducer.view_members_popup == true && <ViewOrganisationMembersPopup options={dropdownReducer?.organisation_roles} />}
    </AuthLayout>
}

export default ArchivedProjects;
